import { getErrorMessage } from '../errors';

const DEFAULT_TTL_MS = 5 * 60 * 1000;
const LONG_TTL_MS = 60 * 60 * 1000;

type CacheEntry<T> = {
  timestamp: number;
  data: T;
  version?: string;
};

function getCacheKey(key: string): string {
  return `api_cache:${key}`;
}

function readCache<T>(key: string): CacheEntry<T> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(getCacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || !Number.isFinite(parsed.timestamp)) return null;
    return parsed;
  } catch (error) {
    console.warn('⚠️ Failed to read API cache:', key, error);
    return null;
  }
}

function writeCache<T>(key: string, data: T): void {
  if (typeof window === 'undefined') return;
  try {
    const entry: CacheEntry<T> = { timestamp: Date.now(), data };
    localStorage.setItem(getCacheKey(key), JSON.stringify(entry));
  } catch (error) {
    console.warn('⚠️ Failed to write API cache:', key, error);
  }
}

function isFresh(entry: CacheEntry<unknown>, ttlMs: number): boolean {
  return Date.now() - entry.timestamp <= ttlMs;
}

export async function getCachedOrFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
  refreshToken?: () => Promise<string | null>
): Promise<T> {
  const cached = readCache<T>(key);
  if (cached && isFresh(cached, ttlMs)) {
    return cached.data;
  }

  try {
    const data = await fetcher();
    writeCache(key, data);
    return data;
  } catch (error: unknown) {
    const errorMessage = getErrorMessage(error);
    if (errorMessage?.includes('Session expired') && refreshToken) {
      console.log('🔄 Refreshing token and retrying...');
      try {
        await refreshToken();
        const data = await fetcher();
        writeCache(key, data);
        return data;
      } catch (retryError) {
        console.error('❌ Retry failed:', retryError);
      }
    }
    
    if (cached) {
      console.warn('⚠️ Using stale API cache due to fetch error:', key, error);
      return cached.data;
    }
    throw error;
  }
}

export function clearApiCache(prefix: string = ''): void {
  if (typeof window === 'undefined') return;
  try {
    const fullPrefix = `api_cache:${prefix}`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(fullPrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    console.log(`🧹 Cleared ${keysToRemove.length} cache entries with prefix: ${prefix || '(all)'}`);
  } catch (error) {
    console.warn('⚠️ Failed to clear API cache:', error);
  }
}

/**
 * Clear specific cache entry by key
 */
export function clearCacheEntry(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(getCacheKey(key));
  } catch (error) {
    console.warn('⚠️ Failed to clear cache entry:', key, error);
  }
}

/**
 * Invalidate cache entries matching a pattern (supports wildcards with *)
 * Examples: 'events:123:*', 'public-events:*'
 */
export function invalidateCache(pattern: string): void {
  if (pattern.includes('*')) {
    // Wildcard pattern - clear all entries matching prefix
    const prefix = pattern.replace(/\*.*$/, '');
    clearApiCache(prefix);
  } else {
    // Exact match - clear single entry
    clearCacheEntry(pattern);
  }
}

export { LONG_TTL_MS, DEFAULT_TTL_MS };
